import React, { useState } from 'react';
import { format } from 'date-fns';
import { OrderStatus, PersistedOrderPayload } from '@onesunday/domain';
import { X, CheckCircle, ArrowRightCircle } from 'lucide-react';
import { STATUS_STYLES } from '../constants';

const currency = new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' });

interface OrderDrawerProps {
  order: PersistedOrderPayload | null;
  onClose: () => void;
  onUpdateStatus?: (orderId: string, status: OrderStatus) => void;
  busy?: boolean;
}

const StatusPill: React.FC<{ status: OrderStatus }> = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.Completed;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      {status}
    </span>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <dt className="text-xs font-medium uppercase tracking-wide text-admin-subtle mb-1">{label}</dt>
    <dd className="text-sm text-admin-text">{children}</dd>
  </div>
);

export const OrderDrawer: React.FC<OrderDrawerProps> = ({ order, onClose, onUpdateStatus, busy }) => {
  if (!order) return null;

  const totalItems = (order.items || []).reduce((acc, i) => acc + i.quantity, 0);
  const [pendingStatus, setPendingStatus] = useState<OrderStatus | null>(null);

  const createdAtDate = order.createdAt ? new Date(order.createdAt) : null;
  const eventDate = order.eventDate && /^\d{4}-\d{2}-\d{2}$/.test(order.eventDate) ? order.eventDate : '';

  const nextAction = (() => {
    if (!onUpdateStatus) return null;
    if (order.status === 'Pending' || order.status === 'Verifying') {
      return { label: 'Mark as Paid', target: 'Paid' as OrderStatus, Icon: CheckCircle };
    }
    if (order.status === 'Paid' || order.status === 'Late') {
      return { label: 'Mark as Ready', target: 'Ready' as OrderStatus, Icon: ArrowRightCircle };
    }
    if (order.status === 'Ready') {
      return { label: 'Mark Completed', target: 'Completed' as OrderStatus, Icon: CheckCircle };
    }
    return null;
  })();

  return (
    <>
      <div className="fixed inset-0 bg-admin-text/30 z-40 transition-opacity" onClick={onClose} />

      <aside className="fixed inset-y-0 right-0 w-full sm:w-[480px] bg-admin-surface shadow-xl z-50 transform transition-transform duration-200 ease-out flex flex-col border-l border-admin-border">
        {/* Header */}
        <header className="px-6 pt-6 pb-5 border-b border-admin-border">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-wide text-admin-subtle">Order</p>
              <h2 className="mt-1 font-mono text-lg font-semibold text-admin-text truncate">
                {order.orderId}
              </h2>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-admin-subtle hover:text-admin-text p-1.5 rounded-md hover:bg-admin-surface-2 transition"
            >
              <X size={18} />
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <StatusPill status={order.status} />
            {order.source && order.source !== 'onesunday' && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-admin-surface-2 text-admin-muted">
                {order.source}
              </span>
            )}
            {order.yocoTestMode && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-admin-surface-2 text-admin-muted">
                test
              </span>
            )}
          </div>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {/* Amount + key facts */}
          <section className="px-6 py-5 border-b border-admin-border">
            <p className="text-xs font-medium uppercase tracking-wide text-admin-subtle">Amount</p>
            <p className="mt-1 text-3xl font-semibold text-admin-text tracking-tight">
              {currency.format(order.totalAmount)}
            </p>
            <p className="mt-1 text-sm text-admin-muted">
              {totalItems} item{totalItems === 1 ? '' : 's'}
              {order.orderType ? ` · ${order.orderType}` : ''}
            </p>
          </section>

          {/* Customer */}
          <section className="px-6 py-5 border-b border-admin-border">
            <h3 className="text-sm font-semibold text-admin-text mb-4">Customer</h3>
            <dl className="grid grid-cols-1 gap-4">
              <Field label="Name">{order.customer?.name || '—'}</Field>
              {order.customer?.phone && <Field label="Phone">{order.customer.phone}</Field>}
              {order.customer?.email && (
                <Field label="Email">
                  <a href={`mailto:${order.customer.email}`} className="text-admin-accent hover:underline">
                    {order.customer.email}
                  </a>
                </Field>
              )}
              {order.customer?.notes && (
                <Field label="Notes">
                  <span className="whitespace-pre-wrap">{order.customer.notes}</span>
                </Field>
              )}
            </dl>
          </section>

          {/* Schedule */}
          {(eventDate || createdAtDate) && (
            <section className="px-6 py-5 border-b border-admin-border">
              <h3 className="text-sm font-semibold text-admin-text mb-4">Schedule</h3>
              <dl className="grid grid-cols-1 gap-4">
                {eventDate && (
                  <Field label="Event date">
                    {format(new Date(eventDate + 'T00:00:00'), 'EEEE, d MMMM yyyy')}
                  </Field>
                )}
                {createdAtDate && (
                  <Field label="Order placed">
                    {format(createdAtDate, "d MMM yyyy 'at' HH:mm")}
                  </Field>
                )}
                {order.paidAt && (
                  <Field label="Paid">
                    {format(new Date(order.paidAt), "d MMM yyyy 'at' HH:mm")}
                  </Field>
                )}
              </dl>
            </section>
          )}

          {/* Payment */}
          {(order.paymentId || order.paymentInfo?.id) && (
            <section className="px-6 py-5 border-b border-admin-border">
              <h3 className="text-sm font-semibold text-admin-text mb-4">Payment</h3>
              <dl className="grid grid-cols-1 gap-4">
                <Field label="Payment ID">
                  <span className="font-mono text-xs break-all">
                    {order.paymentId || order.paymentInfo?.id}
                  </span>
                </Field>
                {order.paymentInfo?.method?.type && (
                  <Field label="Method">{order.paymentInfo.method.type}</Field>
                )}
                {order.receiptEmailSent && order.receiptEmailTo && (
                  <Field label="Receipt sent to">{order.receiptEmailTo}</Field>
                )}
              </dl>
            </section>
          )}

          {/* Customer email audit trail (any sends from /api/sendCustomerEmail) */}
          {Array.isArray(order.customerEmails) && order.customerEmails.length > 0 && (
            <section className="px-6 py-5 border-b border-admin-border">
              <h3 className="text-sm font-semibold text-admin-text mb-4">
                Customer emails ({order.customerEmails.length})
              </h3>
              <ul className="space-y-3">
                {[...order.customerEmails].reverse().map((entry, idx) => {
                  const sent = entry.sentAt ? new Date(entry.sentAt) : null;
                  return (
                    <li
                      key={idx}
                      className="rounded-md border border-admin-border bg-admin-surface-2 px-3 py-2"
                    >
                      <div className="text-sm text-admin-text font-medium truncate">
                        {entry.subject || '(no subject)'}
                      </div>
                      <div className="mt-0.5 text-xs text-admin-muted flex flex-wrap gap-x-2 gap-y-0.5">
                        <span>to {entry.to}</span>
                        {sent && !isNaN(sent.getTime()) && (
                          <span>· {format(sent, "d MMM yyyy 'at' HH:mm")}</span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* Items */}
          <section className="px-6 py-5">
            <h3 className="text-sm font-semibold text-admin-text mb-4">
              Items ({totalItems})
            </h3>
            <ul className="divide-y divide-admin-border border border-admin-border rounded-lg overflow-hidden">
              {(order.items || []).map((item, idx) => (
                <li key={idx} className="flex items-center justify-between px-4 py-3 bg-admin-surface">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="inline-flex shrink-0 items-center justify-center w-7 h-7 rounded-md bg-admin-surface-2 text-xs font-semibold text-admin-text">
                      {item.quantity}
                    </span>
                    <span className="text-sm text-admin-text truncate">{item.name}</span>
                  </div>
                  <span className="text-sm text-admin-muted shrink-0">
                    {currency.format(item.lineTotal)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </div>

        {/* Footer action */}
        {nextAction && (
          <footer className="px-6 py-4 border-t border-admin-border bg-admin-surface">
            <button
              type="button"
              onClick={() => setPendingStatus(nextAction.target)}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-md bg-admin-accent hover:bg-admin-accent-hover text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-admin-accent/30 focus:ring-offset-2"
            >
              <nextAction.Icon size={16} />
              {nextAction.label}
            </button>
          </footer>
        )}
        {!nextAction && order.status === 'Completed' && (
          <footer className="px-6 py-4 border-t border-admin-border bg-admin-surface text-center text-sm text-admin-muted">
            Order completed
          </footer>
        )}
      </aside>

      {/* Confirmation dialog */}
      {pendingStatus && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-admin-text/40 px-4">
          <div className="bg-admin-surface w-full max-w-sm rounded-lg shadow-xl border border-admin-border overflow-hidden">
            <div className="px-5 pt-5 pb-3">
              <h3 className="text-base font-semibold text-admin-text">
                Set order to {pendingStatus}?
              </h3>
              <p className="mt-1 text-sm text-admin-muted">
                Order <span className="font-mono">{order.orderId}</span> will be updated immediately.
              </p>
            </div>
            <div className="px-5 py-3 bg-admin-surface-2 border-t border-admin-border flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingStatus(null)}
                className="px-3 py-1.5 rounded-md text-sm font-medium text-admin-text hover:bg-admin-surface transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (onUpdateStatus) onUpdateStatus(order.orderId, pendingStatus);
                  setPendingStatus(null);
                }}
                disabled={busy}
                className="px-3 py-1.5 rounded-md bg-admin-accent hover:bg-admin-accent-hover text-white text-sm font-semibold transition-colors disabled:opacity-60"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
